import { Alert, AlertTitle, Button, Grid } from '@mui/material';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { useMutation } from '@redwoodjs/web';

import { PasswordInput } from 'src/components/FormikComponents';
import SubCard from 'src/components/SubCard/SubCard';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { useAuthContext } from 'src/contexts/AuthProvider';
import { CHANGE_PASSWORD_MUTATION } from 'src/graphql/mutations/login.mutation';
import { PASSWORD_REGEX, PASSWORD_REGEX_MESSAGE } from 'src/utils/password-utils';

const ChangePasswordFormValidation = Yup.object({
    currentPassword: Yup.string().required('Password is required'),
    newPassword: Yup.string()
        .matches(PASSWORD_REGEX, PASSWORD_REGEX_MESSAGE)
        .required('Password is required'),
    confirmPassword: Yup.string()
        .required('Confirm your password')
        .oneOf([Yup.ref('newPassword')], 'Your passwords do not match'),
});

interface ChangePasswordFormData {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

const ChangePassword = () => {
    const { logout } = useAuthContext();

    const [changePassword, { loading }] = useMutation(CHANGE_PASSWORD_MUTATION, {
        onCompleted: () => {
            showSuccessToast('Password changed successfully');
            logout();
        },
        onError: (error) => {
            showErrorToast(error?.message || 'Error changing password');
        },
    });

    const onChangePassword = async (
        values: ChangePasswordFormData,
        { setSubmitting }: FormikHelpers<ChangePasswordFormData>
    ) => {
        setSubmitting(true);
        await changePassword({
            variables: {
                currentPassword: values.currentPassword,
                newPassword: values.newPassword,
            },
        });
    };

    return (
        <SubCard sx={{ mt: 2 }} title="Change Password">
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Alert
                        severity="warning"
                        variant="outlined"
                        sx={{ borderColor: 'warning.dark' }}
                    >
                        <AlertTitle>Alert!</AlertTitle>
                        {PASSWORD_REGEX_MESSAGE}
                        &nbsp;
                        <strong>For security purposes, please do not share your password.</strong>
                    </Alert>
                </Grid>
                <Grid item xs={12}>
                    <SubCard>
                        <Formik
                            initialValues={{
                                currentPassword: '',
                                newPassword: '',
                                confirmPassword: '',
                            }}
                            validationSchema={ChangePasswordFormValidation}
                            onSubmit={onChangePassword}
                        >
                            {({ dirty, isValid, resetForm, isSubmitting }) => (
                                <Form noValidate autoComplete="off">
                                    <Grid container spacing={2} sx={{ mb: 1 }}>
                                        <Grid item xs={12}>
                                            <PasswordInput
                                                label="Current Password"
                                                name="currentPassword"
                                                placeholder="Current Password"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <PasswordInput
                                                label="New Password"
                                                name="newPassword"
                                                placeholder="New Password"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <PasswordInput
                                                label="Confirm Password"
                                                name="confirmPassword"
                                                placeholder="Confirm Password"
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid spacing={2} container justifyContent="flex-end" mt={1}>
                                        <Grid item>
                                            <Button
                                                variant="outlined"
                                                onClick={() => resetForm()}
                                                disabled={isSubmitting || !dirty}
                                                sx={{
                                                    borderColor: '#000',
                                                    color: '#000',
                                                    '&:hover': {
                                                        bgcolor: '#000',
                                                        color: '#fff',
                                                        opacity: 0.8,
                                                    },
                                                }}
                                            >
                                                Clear
                                            </Button>
                                        </Grid>
                                        <Grid item>
                                            <Button
                                                variant="contained"
                                                type="submit"
                                                disabled={
                                                    isSubmitting || !dirty || !isValid || loading
                                                }
                                                color="primary"
                                                sx={{
                                                    bgcolor: '#000',
                                                    color: '#fff',
                                                    '&:hover': {
                                                        bgcolor: '#000',
                                                        color: '#fff',
                                                        opacity: 0.8,
                                                    },
                                                }}
                                            >
                                                {loading
                                                    ? 'Updating Password...'
                                                    : 'Change Password'}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Form>
                            )}
                        </Formik>
                    </SubCard>
                </Grid>
            </Grid>
        </SubCard>
    );
};

export default ChangePassword;
